import { useState } from "react";
import type { PatternDay, RotaDay, SaveException, StaffDayKind } from "@dentacore/shared-types";
import { STAFF_DAY_KINDS } from "@dentacore/shared-types";

import { Modal } from "../ui/modal";
import { describeNumberError, NumberField } from "../ui/number-field";
import type { NumberFieldRules } from "../ui/number-field";
import { dayAndMonth, shiftText } from "./roster";
import styles from "./staff.module.css";
import { KIND_LABELS, WEEKDAY_NAMES } from "./weekdays";
import { RequiredMark } from "../ui/required-mark";

/** Zero to eight hours — the bounds the rota accepts, not a default. */
const BREAK: NumberFieldRules = { min: 0, max: 480, integer: true, unit: "minutes" };

/**
 * One date's hours, changed from the person's week (ST12, STORY-092).
 *
 * **This is the week that happened, not the week that repeats**: an exception
 * beats the pattern outright for its date, including on a day the pattern has
 * off. Removing one restores the pattern by deleting, not by copying back.
 *
 * **Explicit save, not save-as-you-type** — a time input fires on every
 * keystroke, so `09:0` would be written on the way to `09:00`. It opens on what
 * the day is now, so changing the finish time is not retyping the whole day.
 */
export function DayDialog({
  day,
  name,
  usual,
  busy,
  onSave,
  onClear,
  onPromote,
  onClose,
}: {
  day: RotaDay;
  name: string;
  /** What this weekday is every week, when known. */
  usual: PatternDay | undefined;
  busy: boolean;
  onSave: (day: SaveException) => void;
  onClear: () => void;
  onPromote: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const [kind, setKind] = useState<StaffDayKind>(day.kind);
  const [startsAt, setStartsAt] = useState(day.startsAt);
  const [endsAt, setEndsAt] = useState(day.endsAt);
  /**
   * **Held as text, saved as a number** (QA20-13) — `Number(value) || 0` once
   * turned a mistyped `2e` into a silent zero-minute break.
   */
  const [breakInput, setBreakInput] = useState(String(day.breakMinutes));
  const breakError = describeNumberError(breakInput, BREAK);
  const [note, setNote] = useState(day.note ?? "");

  const label = WEEKDAY_NAMES[day.dayOfWeek] ?? "";

  /**
   * A day given hours it did not have opens on something usable: switching an
   * off day to Clinic would otherwise save a working day with no hours, which
   * reads back as zero paid minutes and looks like the save failed.
   */
  function changeKind(next: StaffDayKind): void {
    setKind(next);
    if (next === "OFF") {
      setStartsAt(null);
      setEndsAt(null);
      setBreakInput("0");
      return;
    }
    if (startsAt === null) setStartsAt(day.startsAt ?? usual?.startsAt ?? "09:00");
    if (endsAt === null) setEndsAt(day.endsAt ?? usual?.endsAt ?? "17:00");
  }

  return (
    <Modal
      label={`Change ${label} ${dayAndMonth(day.date)}`}
      onClose={onClose}
      panelClassName={`${styles.dialog ?? ""} ${styles.dayDialog ?? ""}`}
      testId="day-editor"
    >
      <div className={styles.dialogHead}>
        <div className={styles.dialogTitles}>
          <h2 className={styles.dialogTitle}>
            {label} {dayAndMonth(day.date)}
          </h2>
          <p className={styles.dialogSub}>
            {name}
            {usual !== undefined &&
              ` · every week: ${usual.kind === "OFF" ? "off" : shiftText(usual)}`}
          </p>
        </div>
        <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
          &times;
        </button>
      </div>

      <div className={styles.dayFields}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            Day
            <RequiredMark />
          </span>
          <select
            className={styles.input}
            value={kind}
            disabled={busy}
            aria-label={`${label} day type`}
            onChange={(event) => changeKind(event.target.value as StaffDayKind)}
            data-testid="exception-kind"
          >
            {STAFF_DAY_KINDS.map((option) => (
              <option key={option} value={option}>
                {KIND_LABELS[option]}
              </option>
            ))}
          </select>
        </label>

        {kind !== "OFF" && (
          <>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>
                Starts
                <RequiredMark />
              </span>
              <input
                type="time"
                className={styles.input}
                value={startsAt ?? ""}
                disabled={busy}
                aria-label={`${label} start`}
                onChange={(event) => setStartsAt(event.target.value || null)}
                data-testid="exception-start"
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>
                Ends
                <RequiredMark />
              </span>
              <input
                type="time"
                className={styles.input}
                value={endsAt ?? ""}
                disabled={busy}
                aria-label={`${label} end`}
                onChange={(event) => setEndsAt(event.target.value || null)}
                data-testid="exception-end"
              />
            </label>
            <NumberField
              label="Break, minutes"
              className={styles.field}
              labelClassName={styles.fieldLabel}
              inputClassName={styles.input}
              value={breakInput}
              onChange={setBreakInput}
              rules={BREAK}
              disabled={busy}
              testId="exception-break"
            />
          </>
        )}
      </div>

      {/*
        Optional, and the one thing a rota cannot work out for itself. "Off" on
        a Thursday says nothing; "at the implant course" says why nobody should
        try to book around it.
      */}
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Why</span>
        <input
          type="text"
          className={styles.input}
          maxLength={200}
          value={note}
          disabled={busy}
          placeholder="What a rota cannot infer"
          aria-label={`${label} reason`}
          onChange={(event) => setNote(event.target.value)}
          data-testid="exception-note"
        />
      </label>

      {/*
        Offered only on a day that differs. On a day following the pattern the
        buttons would do nothing — and *Make this the regular day* promotes what
        is saved, not what is typed above it (STORY-093).
      */}
      {day.fromException && (
        <div className={styles.dialogRow}>
          <button
            type="button"
            className={styles.ghost}
            disabled={busy}
            onClick={onClear}
            data-testid="exception-clear"
          >
            Back to the regular hours
          </button>
          <button
            type="button"
            className={styles.ghost}
            disabled={busy}
            onClick={onPromote}
            data-testid="exception-promote"
          >
            Make this the regular {label}
          </button>
        </div>
      )}

      <div className={styles.dialogFoot}>
        <button type="button" className={styles.ghost} onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.primary}
          // A day off has no break to get wrong, so a bad value only blocks
          // saving when it is actually going to be written.
          disabled={busy || (kind !== "OFF" && breakError !== null)}
          onClick={() =>
            onSave({
              kind,
              startsAt: kind === "OFF" ? null : startsAt,
              endsAt: kind === "OFF" ? null : endsAt,
              breakMinutes: kind === "OFF" || breakInput.trim() === "" ? 0 : Number(breakInput),
              note: note.trim() === "" ? null : note.trim(),
            })
          }
          data-testid="exception-save"
        >
          Save this day
        </button>
      </div>
    </Modal>
  );
}
